import { isValid } from "date-fns";
import { useId, useState, type SubmitEvent } from "react";
import { useTranslation } from "react-i18next";

import { useUpdateEvent } from "@/api/bale";
import { isValidationErrorDetail } from "@/compat";
import { Card, CardActionsHeader, CardTextContent } from "@/components/Card";
import { DragHandle } from "@/components/drag-columns/components/DragItem";
import { EndTimeInput } from "@/components/EndTimeInput";
import { ErrorDetail } from "@/components/ErrorDetail";
import { Form } from "@/components/Form";
import { FormField } from "@/components/FormField";
import { IconButton } from "@/components/IconButton";
import { Input } from "@/components/Input";
import { RichTextarea } from "@/components/RichTextarea";
import { StartTimeInput } from "@/components/StartTimeInput";
import { TimeSpanInputProvider } from "@/components/TimeSpanInputProvider";
import { htmlDate } from "@/fp/htmlDate";
import { useCurrentTime } from "@/hooks/useCurrentTime";
import { useCurrentEventSuspense } from "@/pages/EventPage/hooks/useCurrentEvent";

type Props = {
  onStopEditing: () => void;
};

export function EditingEventSummaryCard({ onStopEditing }: Props) {
  const { t } = useTranslation();
  const now = useCurrentTime("@secondly");
  const event = useCurrentEventSuspense({ query: { select: (data) => data.data.event } });
  const [endTime, setEndTime] = useState(event.data.end_time);
  const endTimeWarning = endTime && isValid(endTime) && endTime < now ? t("this-time-has-passed") : null;

  const updateEvent = useUpdateEvent({ mutation: { onSuccess: () => onStopEditing() } });
  function handleSubmit(submitEvent: SubmitEvent<HTMLFormElement>) {
    if (!submitEvent.currentTarget.reportValidity()) {
      return;
    }
    const formData = new FormData(submitEvent.currentTarget);
    const name = formData.get("name")! as string;
    const description = formData.get("description")! as string;
    const location = formData.get("location")! as string;
    const startTime = formData.get("start_time")! as string;
    const endTime = formData.get("end_time")! as string;

    updateEvent.mutate({
      eventId: event.data.id,
      data: {
        name,
        location,
        description,
        max_attendees: event.data.max_attendees ?? null,
        start_time: startTime ? new Date(startTime) : null,
        end_time: endTime ? new Date(endTime) : null,
      },
    });
  }

  const labelId = useId();

  return (
    <Card aria-labelledby={labelId}>
      <Form error={updateEvent.error} onSubmit={handleSubmit} aria-labelledby={labelId}>
        <DragHandle>
          <CardActionsHeader title={t("editing-event")}>
            <IconButton
              type="button"
              name="x"
              aria-label={t("cancel")}
              disabled={updateEvent.isPending}
              onClick={onStopEditing}
            />
            <IconButton type="submit" disabled={updateEvent.isPending} name="save" aria-label={t("save")} />
          </CardActionsHeader>
        </DragHandle>
        <CardTextContent className="flex flex-col gap-4">
          {!!updateEvent.error?.info && !isValidationErrorDetail(updateEvent.error.info) && (
            <ErrorDetail error={updateEvent.error.info} />
          )}
          <FormField title={t("event-name")}>
            <Input
              name="name"
              defaultValue={event.data.name}
              placeholder={t("event-name")}
              disabled={updateEvent.isPending}
            />
          </FormField>
          <FormField title={t("event-description")}>
            <RichTextarea
              name="description"
              defaultValue={event.data.description.raw ?? ""}
              placeholder={t("event-description-placeholder")}
              disabled={updateEvent.isPending}
            />
          </FormField>
          <TimeSpanInputProvider>
            <FormField title={t("event-start")}>
              <StartTimeInput
                name="start_time"
                defaultValue={event.data.start_time ? htmlDate(event.data.start_time) : ""}
                disabled={updateEvent.isPending}
              />
            </FormField>
            <FormField
              className="grow"
              title={
                <div className="flex flex-row">
                  {t("event-end")}
                  {endTimeWarning && <span className="ml-auto text-danger">{endTimeWarning}</span>}
                </div>
              }
            >
              <EndTimeInput
                name="end_time"
                defaultValue={event.data.end_time ? htmlDate(event.data.end_time) : ""}
                onChange={(event) => setEndTime(new Date(event.currentTarget.value))}
                disabled={updateEvent.isPending}
              />
            </FormField>
          </TimeSpanInputProvider>
          <FormField title={t("event-where")}>
            <Input
              type="text"
              name="location"
              defaultValue={event.data.location ?? ""}
              placeholder={t("event-where-placeholder")}
              disabled={updateEvent.isPending}
            />
          </FormField>
        </CardTextContent>
      </Form>
    </Card>
  );
}
